import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Global Event Listener Bus for Toast Notifications
const listeners = new Set();

export const showApiToast = (toast) => {
  listeners.forEach(listener => listener(toast));
};

// Setup Axios Interceptor for Login Quota Toast ONLY (No general API response toasts)
let interceptorsSetup = false;

export const setupAxiosInterceptors = () => {
  if (interceptorsSetup) return;
  interceptorsSetup = true;

  axios.interceptors.response.use(
    (response) => {
      try {
        const fullUrl = response.config?.url || '';

        // Trigger Toast ONLY for Login Quota & Pending Limits Status
        if (fullUrl.includes('/login') && response.data?.quota_info) {
          const q = response.data.quota_info;
          let title = '👋 Welcome Back!';
          let details = `Login Successful. Quota Status: ${q.executions_pending}/${q.execution_limit} test executions pending | ${q.ai_credits_pending?.toLocaleString()}/${q.ai_credit_limit?.toLocaleString()} AI credits available.`;
          let type = 'success';

          if (q.is_limit_reached) {
            title = '⚠️ QUOTA LIMIT REACHED';
            details = `Limit Reached! Executions Pending: 0/${q.execution_limit}. AI Credits Pending: ${q.ai_credits_pending?.toLocaleString()}/${q.ai_credit_limit?.toLocaleString()}. Contact admin to upgrade.`;
            type = 'error';
          }

          showApiToast({
            id: Date.now() + Math.random(),
            type,
            statusCode: response.status,
            title,
            message: details,
            timestamp: new Date().toLocaleTimeString()
          });
        }
      } catch (e) {
        console.error('Login quota toast error:', e);
      }
      return response;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

export default function GlobalToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    setupAxiosInterceptors();

    const handleNewToast = (newToast) => {
      setToasts(prev => [newToast, ...prev.slice(0, 2)]);

      // Auto dismiss after 5 seconds
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== newToast.id));
      }, 5000);
    };

    listeners.add(handleNewToast);
    return () => {
      listeners.delete(handleNewToast);
    };
  }, []);

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '24px',
        right: '24px',
        zIndex: 999999,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        maxWidth: '420px',
        width: 'calc(100vw - 48px)',
        pointerEvents: 'none'
      }}
    >
      {toasts.map(toast => (
        <div
          key={toast.id}
          style={{
            pointerEvents: 'auto',
            background: toast.type === 'success' ? '#059669' : '#dc2626',
            color: '#ffffff',
            padding: '14px 18px',
            borderRadius: '12px',
            boxShadow: '0 12px 35px rgba(0,0,0,0.4), 0 4px 12px rgba(0,0,0,0.25)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '14px',
            animation: 'toastSlideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
          }}
        >
          <div style={{ fontSize: '20px', lineHeight: 1, marginTop: '2px' }}>
            {toast.type === 'success' ? '🟢' : '🔴'}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
              <span style={{ fontSize: '14px', fontWeight: '800', letterSpacing: '-0.2px' }}>
                {toast.title}
              </span>
              <span style={{ fontSize: '10.5px', opacity: 0.75, whiteSpace: 'nowrap' }}>{toast.timestamp}</span>
            </div>

            <div style={{ fontSize: '12.5px', opacity: 0.95, lineHeight: '1.4', wordBreak: 'break-word', fontWeight: '500' }}>
              {toast.message}
            </div>
          </div>

          <button
            onClick={() => removeToast(toast.id)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#ffffff',
              opacity: 0.75,
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold',
              padding: '0 2px',
              lineHeight: 1,
              marginTop: '-2px'
            }}
            title="Dismiss"
          >
            ×
          </button>
        </div>
      ))}
      <style>{`
        @keyframes toastSlideIn {
          from { opacity: 0; transform: translateX(60px) scale(0.92); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
